import React from 'react';
import { CircleCheckBig } from 'lucide-react';
import { useReadyTranslation } from '../hooks/useReadyTranslation';

/* TANDA FESTEJO-PACKS — EL FESTEJO DE UNA COMPRA DE PAQUETE.
 *
 * QUÉ ES. Hasta aquí, comprar créditos terminaba en silencio: en web el retorno
 * de Stripe refrescaba el panel sin decir nada (Dashboard.js, el efecto del
 * `?credits_purchased=1`) y en la app la hoja de Apple se cerraba y el modal de
 * paquetes desaparecía. El usuario acababa de pagar y la única señal era que el
 * contador de créditos cambiaba de número. Esto pone la palomita.
 *
 * DE DÓNDE SALE EL DISEÑO. No se inventa nada: es el MISMO modal de éxito que
 * ya pinta el upgrade de suscripción (`pages/PricingPage.js:899-945`), clase por
 * clase — la cortina `fixed inset-0 z-50` con su desenfoque, la tarjeta
 * `bg-[#111] border-[#262626] rounded-[20px]`, la `CircleCheckBig` esmeralda de
 * 40px y el título en `text-xl`. Aquel modal es la referencia firmada, así que
 * copiarlo (y no parecerse a él) es el punto.
 *
 * LAS TRES DIFERENCIAS, TODAS FIRMADAS:
 *   1. SIN CUERPO. El de upgrade explica el prorrateo; aquí no hay nada que
 *      explicar. Por eso el título se queda con el `mb-6` que allá llevaba el
 *      párrafo: el ritmo hasta el botón es el mismo.
 *   2. SIN NÚMERO DE CRÉDITOS. Genérico A PROPÓSITO. Un "15 créditos" aquí
 *      tendría que salir de la respuesta del backend (web no la tiene: vuelve de
 *      Stripe por URL) y en la app mentiría en cuanto Apple entregue un recibo
 *      viejo junto con el nuevo.
 *   3. UN SOLO BOTÓN. El "Got it"/"Entendido" del modal de upgrade, con SU clave
 *      (`pricing:cta.got_it`) y SU estilo. No hay "Ir al Panel" porque este modal
 *      se pinta ENCIMA del panel en web y encima de la pantalla de compra en la
 *      app: no hay a dónde llevar a nadie.
 *
 * LOS 44pt. El botón de upgrade es `h-10` (40px) porque allá va en pareja y la
 * fila entera se lee como un bloque; aquí es el ÚNICO destino táctil del modal,
 * así que lleva `minHeight: 44` — el mismo remedio, y con la misma sintaxis, que
 * AS-3 le puso a los suyos (`pages/PricingPage.js:883`). La clase no cambia: el
 * alto sólo sube si la pantalla lo apretaba por debajo de 44.
 *
 * DÓNDE SE MONTA. En las superficies, nunca en el puente (`lib/purchases.js`):
 *   · web  — `pages/Dashboard.js`, en el retorno de Stripe.
 *   · app  — `components/CreditPackModal.js`, cuando /verify acredita de verdad.
 * El cartero de fondo (recibos rescatados al arrancar o tras un login) NO pinta
 * esto: nadie le pidió nada al usuario en ese momento y su contrato es la
 * bitácora de consola. "Restaurar compras" conserva sus toasts.
 *
 * i18n: pide su namespace al montarse, y como sólo se monta cuando hay algo que
 * festejar, ninguna pantalla estrena una espera nueva en su puerta de `ready`.
 * Mientras el namespace viaja no se pinta nada (null, no una cortina en negro):
 * el festejo llega 100 ms tarde en el peor caso, que es infinitamente mejor que
 * tapar la pantalla con un spinner a pantalla completa.
 */
export default function PackSuccessModal({ onClose }) {
  const { t, ready } = useReadyTranslation(['pricing']);
  if (!ready) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="pack-success-modal"
    >
      <div className="bg-[#111] border border-[#262626] rounded-[20px] p-6 sm:p-8 max-w-md w-full mx-4 shadow-2xl text-center">
        <CircleCheckBig className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-6" data-testid="pack-success-title">
          {t('pricing:modal.pack_success.title')}
        </h3>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl h-10 text-sm transition-colors"
            style={{ minHeight: 44 }}
            data-testid="pack-success-dismiss"
          >
            {t('pricing:cta.got_it')}
          </button>
        </div>
      </div>
    </div>
  );
}
